import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Native-scroll carousel (CSS scroll-snap) instead of a JS drag simulation —
// this gets real momentum/trackpad/touch scrolling for free from the browser,
// which is both smoother and more accessible than react-owl-carousel's jQuery
// drag handling ever was. Framer Motion is used for the chrome around it
// (arrow press feedback, active-dot growth), not for driving the scroll itself.
const Carousel = ({
  children,
  itemClassName = "w-full",
  gap = "gap-4",
  showArrows = true,
  showDots = false,
  autoplay = false,
  autoplayInterval = 3800,
  className = "",
}) => {
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const items = React.Children.toArray(children);

  const closestIndex = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 0;
    let closest = 0;
    let closestDist = Infinity;
    Array.from(track.children).forEach((child, i) => {
      const dist = Math.abs(child.offsetLeft - track.scrollLeft);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    });
    return closest;
  }, []);

  const scrollToIndex = useCallback((index) => {
    const track = trackRef.current;
    const child = track?.children?.[index];
    if (!track || !child) return;
    track.scrollTo({ left: child.offsetLeft - track.offsetLeft, behavior: "smooth" });
  }, []);

  const scrollByDir = useCallback((dir) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: track.clientWidth * 0.9 * dir, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setActiveIndex(closestIndex());
        raf = null;
      });
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      track.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [closestIndex]);

  useEffect(() => {
    if (!autoplay || items.length <= 1) return undefined;
    const id = setInterval(() => {
      const track = trackRef.current;
      if (!track) return;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      if (atEnd) track.scrollTo({ left: 0, behavior: "smooth" });
      else scrollByDir(1);
    }, autoplayInterval);
    return () => clearInterval(id);
  }, [autoplay, autoplayInterval, items.length, scrollByDir]);

  return (
    <div className={"relative " + className}>
      <div
        ref={trackRef}
        className={
          `flex ${gap} overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 ` +
          "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        }
      >
        {items.map((child, i) => (
          <div key={i} className={`snap-start shrink-0 ${itemClassName}`}>
            {child}
          </div>
        ))}
      </div>

      {showArrows && items.length > 1 && (
        <>
          <motion.button
            type="button"
            aria-label="Previous"
            whileTap={{ scale: 0.9 }}
            onClick={() => scrollByDir(-1)}
            className="absolute left-1 top-1/2 -translate-y-1/2 hidden sm:flex h-9 w-9 items-center justify-center rounded-full bg-white/95 border border-slate-200 shadow-md hover:bg-white z-10"
          >
            <ChevronLeft className="h-5 w-5 text-slate-700" />
          </motion.button>
          <motion.button
            type="button"
            aria-label="Next"
            whileTap={{ scale: 0.9 }}
            onClick={() => scrollByDir(1)}
            className="absolute right-1 top-1/2 -translate-y-1/2 hidden sm:flex h-9 w-9 items-center justify-center rounded-full bg-white/95 border border-slate-200 shadow-md hover:bg-white z-10"
          >
            <ChevronRight className="h-5 w-5 text-slate-700" />
          </motion.button>
        </>
      )}

      {showDots && items.length > 1 && (
        <div className="mt-2 flex items-center justify-center gap-1.5">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => scrollToIndex(i)}
              className={
                "h-1.5 rounded-full transition-all " +
                (i === activeIndex ? "w-5 bg-brand-600" : "w-1.5 bg-slate-300")
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
